"use client";

import * as React from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}
export default function ContactInformation() {
  const form = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: ""
    }
  });
  const onSubmit = (data: ContactFormData) => {
    console.log(data);
  };
  const contactInfo = [{
    icon: MapPin,
    label: "Address",
    value: "5 Mill Lane, Warmsworth, Doncaster DN4 9RG\n, UK",
    href: "https://share.google/5ggMi61sd67iHqoma"
  }, {
    icon: Phone,
    label: "Phone",
    value: "07463682889",
    href: "tel:+447463682889"
  }, {
    icon: Mail,
    label: "Email",
    value: "appointments@alignphysiotherapy.co.uk",
    href: "mailto:appointments@alignphysiotherapy.co.uk"
  }];
  return <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4 tracking-wide">
            Get In Touch
          </h2>
          <p className="font-sans text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Contact us today to schedule your appointment or ask any questions about our services.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Contact Information Cards */}
          <div className="space-y-6 animate-fade-in-up">
            {contactInfo.map((item, i) => {
            const Icon = item.icon;
            return <Card key={i} data-index={i} className="border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-0.5">
                  <CardContent className="flex items-start gap-4 p-6">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg font-semibold text-foreground mb-1">
                        {item.label}
                      </h3>
                      <Link to={item.href} className="font-sans text-base text-accent hover:text-accent/80 transition-colors duration-150 break-words">
                        {item.value}
                      </Link>
                    </div>
                  </CardContent>
                </Card>;
          })}

            {/* Opening Hours */}
            <Card className="border border-border shadow-soft">
              <CardHeader>
                <CardTitle className="font-display text-xl font-semibold text-foreground">
                  Opening Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-foreground">Monday - Friday:</span>
                  <span className="text-muted-foreground">9:00 am - 9:00 pm</span>
                </div>
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-foreground">Saturday:</span>
                  <span className="text-muted-foreground">9:00 am - 1:00 pm</span>
                </div>
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-foreground">Sunday:</span>
                  <span className="text-muted-foreground">Closed</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="border border-border shadow-soft animate-fade-in-up-delay-100">
            <CardHeader>
              <CardTitle className="font-display text-2xl font-bold text-foreground">
                Send Us a Message
              </CardTitle>
              <CardDescription className="font-sans text-base text-muted-foreground">
                Fill out the form below and we'll get back to you as soon as possible.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField control={form.control} name="name" rules={{
                  required: "Name is required"
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium text-foreground">
                          Full Name
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="Enter your full name" className="border border-border rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-150" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="email" rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                  }
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium text-foreground">
                          Email Address
                        </FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="your.email@example.com" className="border border-border rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-150" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="phone" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium text-foreground">
                          Phone Number
                        </FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="tel:+447463682889" className="border border-border rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-150" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="message" rules={{
                  required: "Message is required"
                }} render={({
                  field
                }) => <FormItem>
                        <FormLabel className="font-sans text-sm font-medium text-foreground">
                          Message
                        </FormLabel>
                        <FormControl>
                          <Textarea placeholder="Tell us how we can help you..." className="border border-border rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-150 min-h-32" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <Button type="submit" className="w-full bg-primary text-primary-foreground font-medium py-3 px-6 rounded-lg shadow-md hover:bg-primary/90 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-150 ease-[var(--ease-spring)]">
                    Send Message
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <Card className="border border-border shadow-soft overflow-hidden animate-fade-in-up-delay-200">
          <CardContent className="p-0">
            <div className="w-full h-[400px] md:h-[500px] bg-secondary relative">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2382.723485054366!2d-1.1852538!3d53.4983013!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48790cf3c4490bab%3A0x110445b60975001f!2s5%20Mill%20Ln%2C%20Warmsworth%2C%20Doncaster%20DN4%209RG%2C%20UK!5e0!3m2!1sen!2sus!4v1706296800000!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Align Physiotherapy Location" className="absolute inset-0" />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}